import React, { forwardRef, useCallback } from 'react';
import { View, StyleSheet, FlatList, Dimensions, Image } from 'react-native';
import {
  Text,
  Card,
  useTheme,
  ActivityIndicator,
  Button,
  Chip,
} from 'react-native-paper';
import { useFeaturedRecipes } from '@coffeeplexity/api-client';
import { RecipeCard } from '@coffeeplexity/ui';
import {
  Recipe,
  useRecipePrefsStore,
  getFirstImageUrl,
} from '@coffeeplexity/shared';
import { Star, ArrowRight, Award } from 'lucide-react-native';

interface FeaturedRecipesProps {
  limit?: number;
  showHeader?: boolean;
  showViewAll?: boolean;
  onRecipePress: (recipe: Recipe) => void;
  onViewAll?: () => void;
  variant?: 'grid' | 'carousel';
  onScroll?: (event: any) => void;
}

const { width } = Dimensions.get('window');

export const FeaturedRecipes = forwardRef<FlatList, FeaturedRecipesProps>(
  (
    {
      limit = 6,
      showHeader = true,
      showViewAll = true,
      onRecipePress,
      onViewAll,
      variant = 'grid',
      onScroll,
    },
    ref
  ) => {
    const theme = useTheme();
    const { data, isLoading, error, refetch } = useFeaturedRecipes();

    if (isLoading) {
      return (
        <View style={styles.container}>
          {showHeader && (
            <View style={styles.header}>
              <View style={styles.headerTitle}>
                <Star size={24} color={theme.colors.primary} />
                <Text variant='headlineSmall' style={styles.title}>
                  Featured Recipes
                </Text>
              </View>
            </View>
          )}
          <View style={styles.centered}>
            <ActivityIndicator size='large' color={theme.colors.primary} />
          </View>
        </View>
      );
    }

    if (error || !data?.items) {
      return (
        <View style={styles.container}>
          {showHeader && (
            <View style={styles.header}>
              <View style={styles.headerTitle}>
                <Star size={24} color={theme.colors.primary} />
                <Text variant='headlineSmall' style={styles.title}>
                  Featured Recipes
                </Text>
              </View>
            </View>
          )}
          <View style={styles.centered}>
            <Text
              variant='bodyMedium'
              style={[styles.errorText, { color: theme.colors.error }]}
            >
              Failed to load featured recipes
            </Text>
            <Button mode='contained' onPress={() => refetch()}>
              Try Again
            </Button>
          </View>
        </View>
      );
    }

    const recipes = data.items.filter(Boolean).slice(0, limit);

    const renderRecipe = useCallback(
      ({ item: recipe }: { item: Recipe }) => (
        <View
          style={[
            styles.recipeContainer,
            variant === 'carousel' && styles.carouselRecipe,
          ]}
        >
          <RecipeCard
            recipe={recipe}
            onPress={onRecipePress}
            onLike={(id: string) => {
              useRecipePrefsStore.getState().toggleLike(id);
            }}
            onBookmark={(id: string) => {
              useRecipePrefsStore.getState().toggleBookmark(id);
            }}
            liked={useRecipePrefsStore.getState().isLiked(recipe.id)}
            bookmarked={useRecipePrefsStore.getState().isBookmarked(recipe.id)}
            variant={variant === 'carousel' ? 'featured' : 'default'}
            headerAccessory={
              <View
                style={[
                  styles.featuredBadge,
                  {
                    backgroundColor: theme.dark
                      ? 'rgba(0,0,0,0.45)'
                      : 'rgba(255,255,255,0.9)',
                    borderWidth: 1,
                    borderColor:
                      (theme.colors as any).outlineVariant ||
                      theme.colors.outline,
                    shadowColor: '#000',
                    shadowOpacity: theme.dark ? 0.25 : 0.08,
                    shadowRadius: 6,
                    shadowOffset: { width: 0, height: 3 },
                    elevation: 3,
                  },
                ]}
              >
                <Award size={12} color={theme.colors.primary} />
                <Text
                  variant='labelSmall'
                  style={{ color: theme.colors.onSurface }}
                >
                  Featured
                </Text>
              </View>
            }
            testID={`featured-recipe-${recipe.id}`}
          />
        </View>
      ),
      [variant, onRecipePress, theme]
    );

    const renderHeader = () => (
      <View style={styles.header}>
        <View style={styles.headerTitle}>
          <Star size={24} color={theme.colors.primary} />
          <Text variant='headlineSmall' style={styles.title}>
            Featured Recipes
          </Text>
          <Chip
            mode='flat'
            textStyle={styles.chipText}
            style={[
              styles.chip,
              { backgroundColor: theme.colors.primaryContainer },
            ]}
          >
            Editor's Choice
          </Chip>
        </View>
        {showViewAll && onViewAll && (
          <Button
            mode='text'
            onPress={onViewAll}
            icon={({ size, color }) => <ArrowRight size={size} color={color} />}
            contentStyle={styles.viewAllButton}
          >
            View All
          </Button>
        )}
      </View>
    );

    const renderHeroRecipe = () => {
      if (recipes.length === 0) return null;

      const heroRecipe = recipes[0];
      if (!heroRecipe) return null;

      return (
        <Card
          onPress={() => onRecipePress(heroRecipe)}
          style={[
            styles.heroCard,
            {
              backgroundColor: theme.colors.surface,
              borderWidth: 1,
              borderColor:
                (theme.colors as any).outlineVariant || theme.colors.outline,
            },
          ]}
        >
          <View
            pointerEvents='none'
            style={[
              styles.heroRecipeBadge,
              {
                backgroundColor: theme.dark
                  ? 'rgba(0,0,0,0.45)'
                  : 'rgba(255,255,255,0.9)',
                borderWidth: 1,
                borderColor:
                  (theme.colors as any).outlineVariant || theme.colors.outline,
                shadowColor: '#000',
                shadowOpacity: theme.dark ? 0.25 : 0.08,
                shadowRadius: 6,
                shadowOffset: { width: 0, height: 3 },
                elevation: 2,
              },
            ]}
          >
            <Award size={16} color={theme.colors.primary} />
            <Text
              variant='labelMedium'
              style={{ color: theme.colors.onSurface }}
            >
              Recipe of the Week
            </Text>
          </View>

          <Card.Content style={styles.heroContent}>
            {(() => {
              const firstImage = getFirstImageUrl(heroRecipe.images);
              return firstImage ? (
                <Image
                  source={{ uri: firstImage }}
                  style={styles.heroImage}
                  resizeMode='cover'
                />
              ) : null;
            })()}

            <View style={styles.heroText}>
              <Text
                variant='headlineSmall'
                style={[styles.heroTitle, { color: theme.colors.onSurface }]}
              >
                {heroRecipe.title}
              </Text>
              <Text
                variant='bodyMedium'
                style={[
                  styles.heroDescription,
                  { color: theme.colors.onSurface },
                ]}
                numberOfLines={3}
              >
                {heroRecipe.description}
              </Text>

              <View style={styles.heroStats}>
                <View style={styles.heroStat}>
                  <Star size={16} color={theme.colors.primary} />
                  <Text
                    variant='bodySmall'
                    style={{ color: theme.colors.onSurface }}
                  >
                    {heroRecipe.rating?.toFixed(1) || '0.0'}
                  </Text>
                </View>
                <Text
                  variant='bodySmall'
                  style={{ color: theme.colors.onSurface }}
                >
                  {heroRecipe.review_count || 0} reviews
                </Text>
                <Text
                  variant='bodySmall'
                  style={{ color: theme.colors.onSurface }}
                >
                  {(heroRecipe.view_count || 0).toLocaleString()} views
                </Text>
              </View>

              <Button
                mode='contained'
                style={[
                  styles.heroButton,
                  { backgroundColor: theme.colors.primary },
                ]}
              >
                Try This Recipe
              </Button>
            </View>
          </Card.Content>
        </Card>
      );
    };

    const renderStats = () => (
      <Card
        style={[
          styles.statsCard,
          { backgroundColor: theme.colors.surfaceVariant },
        ]}
      >
        <Card.Content>
          <Text
            variant='bodySmall'
            style={[
              styles.statsDescription,
              { color: theme.colors.onSurfaceVariant },
            ]}
          >
            These recipes have been carefully selected by our culinary experts
          </Text>
          <View style={styles.statsContainer}>
            <View style={styles.statItem}>
              <View style={styles.statValue}>
                <Star size={16} color={theme.colors.primary} />
                <Text
                  variant='titleSmall'
                  style={{ color: theme.colors.primary }}
                >
                  {(
                    recipes.reduce(
                      (sum, recipe) => sum + (recipe.rating || 0),
                      0
                    ) / recipes.length
                  ).toFixed(1)}
                </Text>
              </View>
              <Text
                variant='bodySmall'
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                avg rating
              </Text>
            </View>
            <View style={styles.statItem}>
              <Text
                variant='titleSmall'
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                {recipes.reduce((sum, recipe) => sum + recipe.review_count, 0)}
              </Text>
              <Text
                variant='bodySmall'
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                total reviews
              </Text>
            </View>
          </View>
        </Card.Content>
      </Card>
    );

    if (recipes.length === 0) {
      return (
        <View style={styles.container}>
          {showHeader && renderHeader()}
          <View style={styles.centered}>
            <Star size={48} color={theme.colors.onSurfaceVariant} />
            <Text
              variant='titleMedium'
              style={[
                styles.emptyTitle,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              No featured recipes
            </Text>
            <Text
              variant='bodyMedium'
              style={[
                styles.emptySubtitle,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              Check back later for featured content
            </Text>
          </View>
        </View>
      );
    }

    if (variant === 'carousel') {
      return (
        <View style={styles.container}>
          {showHeader && renderHeader()}
          <FlatList
            ref={ref}
            data={recipes}
            renderItem={renderRecipe}
            keyExtractor={item => item?.id}
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.carouselList}
            ItemSeparatorComponent={() => <View style={styles.separator} />}
            onScroll={onScroll}
            scrollEventThrottle={16}
            removeClippedSubviews={true}
            maxToRenderPerBatch={5}
            initialNumToRender={5}
            windowSize={5}
          />
          {renderStats()}
        </View>
      );
    }

    return (
      <View style={styles.container}>
        {showHeader && renderHeader()}

        {/* Hero Recipe */}
        {renderHeroRecipe()}

        {/* Other Featured Recipes */}
        {recipes.length > 1 && (
          <FlatList
            ref={ref}
            data={recipes.slice(1)}
            renderItem={renderRecipe}
            keyExtractor={item => item?.id}
            contentContainerStyle={styles.list}
            showsVerticalScrollIndicator={false}
            ItemSeparatorComponent={() => <View style={{ height: 0 }} />}
            onScroll={onScroll}
            scrollEventThrottle={16}
            removeClippedSubviews={true}
            maxToRenderPerBatch={10}
            initialNumToRender={10}
            windowSize={10}
          />
        )}

        {renderStats()}
      </View>
    );
  }
);

const styles = StyleSheet.create({
  container: {
    marginBottom: 24,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingHorizontal: 16,
  },
  headerTitle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  title: {
    fontWeight: 'bold',
  },
  chip: {
    height: 24,
  },
  chipText: {
    fontSize: 10,
  },
  viewAllButton: {
    flexDirection: 'row-reverse',
  },
  centered: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 32,
  },
  errorText: {
    marginBottom: 16,
    textAlign: 'center',
  },
  heroCard: {
    marginHorizontal: 16,
    marginBottom: 16,
    position: 'relative',
  },
  heroRecipeBadge: {
    alignSelf: 'flex-end',
    marginTop: 12,
    marginBottom: 12,
    marginRight: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  heroContent: {
    padding: 0,
  },
  heroImage: {
    width: '100%',
    height: 200,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  heroText: {
    padding: 16,
  },
  heroTitle: {
    marginBottom: 8,
    fontWeight: 'bold',
  },
  heroDescription: {
    marginBottom: 12,
    lineHeight: 20,
  },
  heroStats: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    marginBottom: 16,
  },
  heroStat: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  heroButton: {
    alignSelf: 'flex-start',
  },
  list: {
    paddingHorizontal: 16,
  },
  recipeContainer: {
    width: '100%',
    marginBottom: 0,
  },
  carouselRecipe: {
    width: width * 0.8,
    flex: 0,
  },
  carouselList: {
    paddingHorizontal: 16,
  },
  separator: {
    width: 12,
  },
  featuredBadge: {
    alignSelf: 'flex-end',
    marginTop: 8,
    marginRight: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statsCard: {
    marginHorizontal: 16,
    marginTop: 16,
  },
  statsDescription: {
    textAlign: 'center',
    marginBottom: 12,
  },
  statsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  statItem: {
    alignItems: 'center',
  },
  statValue: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  emptyTitle: {
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtitle: {
    textAlign: 'center',
  },
});
